import type {KeyTextField} from '@prismicio/client'
import type {Top10AiToolsSliceDefaultItem} from 'prismicio-types'
import React, {useCallback, useMemo, useState} from 'react'

type SortKey = 'rank' | 'spend' | 'growth'
type SortDir = 'asc' | 'desc'

function barPct(spend: number): number {
  if (!Number.isFinite(spend) || spend <= 0) {
    return 0
  }

  const lo = Math.log(12000)
  const hi = Math.log(3800000)
  const clamped = Math.min(Math.max(spend, 12000), 3800000)

  return Math.round(8 + ((Math.log(clamped) - lo) / (hi - lo)) * 92)
}

interface Props {
  eyebrow: KeyTextField
  title: KeyTextField
  desc: KeyTextField
  tableNote: KeyTextField
  tools: Top10AiToolsSliceDefaultItem[]
}

const Leaderboard: React.FC<Props> = ({eyebrow, title, desc, tableNote, tools}) => {
  const [sortKey, setSortKey] = useState<SortKey>('rank')
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  const setSort = useCallback(
    (key: SortKey) => {
      if (sortKey === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
      } else {
        setSortKey(key)
        setSortDir('asc')
      }
    },
    [sortKey],
  )

  const mark = useCallback(
    (key: SortKey) => {
      if (sortKey !== key) {
        return <span className="t10-th-sort-icon">↕</span>
      }

      return <span className="t10-th-sort-icon t10-th-sort-icon--active">{sortDir === 'asc' ? '↑' : '↓'}</span>
    },
    [sortKey, sortDir],
  )

  const rows = useMemo(() => {
    const sorted = tools.slice()

    const keyMap: Record<SortKey, keyof Top10AiToolsSliceDefaultItem> = {
      rank: 'rank',
      spend: 'spend',
      growth: 'yoy',
    }

    const field = keyMap[sortKey]
    sorted.sort((a, b) => ((a[field] as number) ?? 0) - ((b[field] as number) ?? 0))

    if (sortDir === 'desc') {
      sorted.reverse()
    }

    return sorted
  }, [tools, sortKey, sortDir])

  const ariaSort = useCallback(
    (key: SortKey): 'ascending' | 'descending' | 'none' => {
      if (sortKey !== key) {
        return 'none'
      }

      return sortDir === 'asc' ? 'ascending' : 'descending'
    },
    [sortKey, sortDir],
  )

  const thClass = useCallback(
    (key: SortKey) => (sortKey === key ? 't10-th-btn t10-th-btn--active' : 't10-th-btn'),
    [sortKey],
  )

  return (
    <section className="t10-section t10-section-alt">
      <div className="t10-wrap">
        {eyebrow && <p className="t10-eyebrow">{eyebrow}</p>}
        {title && (
          <h2 className="t10-section-title" style={{maxWidth: '24ch'}}>
            {title}
          </h2>
        )}
        {desc && <p className="t10-section-desc">{desc}</p>}

        <div className="t10-table">
          <div className="t10-table-head">
            <button
              className={thClass('rank')}
              onClick={() => setSort('rank')}
              aria-sort={ariaSort('rank')}
              title="Sort by rank"
            >
              #{mark('rank')}
            </button>
            <div className="t10-th">Tool</div>
            <div className="t10-th">Category</div>
            <button
              className={thClass('spend')}
              onClick={() => setSort('spend')}
              aria-sort={ariaSort('spend')}
              title="Sort by total spend"
            >
              Total spend{mark('spend')}
            </button>
            <button
              className={thClass('growth')}
              onClick={() => setSort('growth')}
              aria-sort={ariaSort('growth')}
              title="Sort by year-on-year growth"
            >
              YoY{mark('growth')}
            </button>
          </div>

          {rows.map((row, i) => (
            <div key={`${row.rank ?? i}-${row.name ?? ''}`} className="t10-row">
              <div className="t10-row-rank">{row.rank}</div>
              <div className="t10-row-tool">
                <div className="t10-row-name">{row.name}</div>
                {row.product && <div className="t10-row-product">{row.product}</div>}
              </div>
              <div className="t10-row-cat">{row.cat}</div>
              <div className="t10-row-spend">
                <div className="t10-spend-val">{row.spend_str}</div>
                <div className="t10-spend-bar">
                  <div className="t10-spend-fill" style={{width: `${barPct(row.spend ?? 0)}%`}} />
                </div>
              </div>
              <div className="t10-row-yoy" style={{color: (row.yoy ?? 0) < 0 ? '#FF7A7A' : '#B4FF44'}}>
                {row.yoy_str}
              </div>
            </div>
          ))}
        </div>

        {tableNote && <p className="t10-table-note">{tableNote}</p>}
      </div>
    </section>
  )
}

export default Leaderboard
